/**
 * Solapa "Actas" de Recepción: actas cerradas (confirmadas, parciales, con
 * errores, anuladas) y en confirmación del GLN activo. Click en una fila abre
 * el acta. Desde acá sale el export a Excel para informar a Plataforma: la
 * recepción no tiene vínculo con el ERP, así que lo confirmado en ANMAT se
 * carga a mano con esta planilla.
 */

import { useState } from 'react';
import { CheckCircle2, Download, Loader2, RefreshCw, Search, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/notify';
import { fmtDateTime } from '@/lib/format';
import { descargarExcelHojas } from '@/lib/excel';
import { useExportarRecepcionesMutation, useRecepcionCompletadas } from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import type { RecepcionCompletada } from '@/api/contracts/recepcion-completadas';
import { ESTADO_ACTA_LABEL, type EstadoActaRecepcion } from '@/domain/types';
import {
  BADGE_ESTADO_ACTA,
  EXPORT_SERIES_HEADERS,
  EXPORT_SOBRANTES_HEADERS,
  filasExportSeries,
  filasExportSobrantes,
} from './acta-helpers';

const ESTADOS_FILTRO: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'todas', label: 'Todas' },
  { value: 'trazado', label: ESTADO_ACTA_LABEL.trazado },
  { value: 'parcial', label: ESTADO_ACTA_LABEL.parcial },
  { value: 'error', label: ESTADO_ACTA_LABEL.error },
  { value: 'confirmando', label: ESTADO_ACTA_LABEL.confirmando },
  { value: 'anulada', label: ESTADO_ACTA_LABEL.anulada },
];

interface Props {
  onAbrirActa?: (actaId: number) => void;
}

export function CompletadasRecepcionTable({ onAbrirActa }: Props = {}) {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);

  const [textoInput, setTextoInput] = useState('');
  const [dateFromInput, setDateFromInput] = useState('');
  const [dateToInput, setDateToInput] = useState('');
  const [estadoInput, setEstadoInput] = useState('todas');
  const [aplicados, setAplicados] = useState<{
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    estado?: string;
  }>({});

  const { data, isLoading, error, refetch, isFetching } = useRecepcionCompletadas(
    ubicacionActivaId,
    aplicados,
  );
  const exportar = useExportarRecepcionesMutation();

  const items = data?.items ?? [];
  const hayFiltros = Boolean(
    aplicados.search || aplicados.dateFrom || aplicados.dateTo || aplicados.estado,
  );

  const aplicar = (e: React.FormEvent) => {
    e.preventDefault();
    setAplicados({
      search: textoInput.trim() || undefined,
      dateFrom: dateFromInput || undefined,
      dateTo: dateToInput || undefined,
      estado: estadoInput === 'todas' ? undefined : estadoInput,
    });
  };

  const limpiar = () => {
    setTextoInput('');
    setDateFromInput('');
    setDateToInput('');
    setEstadoInput('todas');
    setAplicados({});
  };

  const onExportar = () => {
    if (!ubicacionActivaId) return;
    exportar.mutate(
      {
        glnId: ubicacionActivaId,
        filters: {
          dateFrom: aplicados.dateFrom,
          dateTo: aplicados.dateTo,
          estado: aplicados.estado,
        },
      },
      {
        onSuccess: (res) => {
          if (res.series.length === 0 && res.sobrantes.length === 0) {
            toast.warning('No hay series para exportar con los filtros aplicados.');
            return;
          }
          const hoy = new Date().toISOString().slice(0, 10);
          const rango = [aplicados.dateFrom, aplicados.dateTo].filter(Boolean).join('_a_');
          void descargarExcelHojas(`recepciones_${rango || hoy}.xlsx`, [
            { nombre: 'Series', headers: EXPORT_SERIES_HEADERS, rows: filasExportSeries(res) },
            {
              nombre: 'Sobrantes',
              headers: EXPORT_SOBRANTES_HEADERS,
              rows: filasExportSobrantes(res),
            },
          ])
            .then(() =>
              toast.success(
                `Export listo · ${res.series.length} series, ${res.sobrantes.length} sobrantes`,
              ),
            )
            .catch((e: unknown) =>
              toast.error(
                `No se pudo generar el archivo: ${e instanceof Error ? e.message : String(e)}`,
              ),
            );
        },
        onError: (e) => toast.error(`No se pudo exportar: ${e.message}`),
      },
    );
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span>Consultando ingresos realizados…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          No se pudieron obtener las actas: {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={aplicar} className="flex flex-wrap items-end gap-2">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={textoInput}
                onChange={(e) => setTextoInput(e.target.value)}
                placeholder="Remito, factura, emisor o GLN"
                className="pl-9 font-mono"
              />
            </div>
            <Input
              type="date"
              value={dateFromInput}
              onChange={(e) => setDateFromInput(e.target.value)}
              title="Desde (fecha de cierre)"
              className="w-[160px]"
            />
            <Input
              type="date"
              value={dateToInput}
              onChange={(e) => setDateToInput(e.target.value)}
              title="Hasta (fecha de cierre)"
              className="w-[160px]"
            />
            <Select value={estadoInput} onValueChange={setEstadoInput}>
              <SelectTrigger className="w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ESTADOS_FILTRO.map((e) => (
                  <SelectItem key={e.value} value={e.value}>
                    {e.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="submit"
              disabled={isFetching}
              className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
            >
              {isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span className="ml-1.5">Buscar</span>
            </Button>
            {hayFiltros && (
              <Button type="button" variant="outline" onClick={limpiar} title="Limpiar filtros">
                <X className="h-4 w-4" />
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-600">
          {items.length} ingreso{items.length === 1 ? '' : 's'} realizado{items.length === 1 ? '' : 's'}
          {data?.total !== undefined && data.total > items.length
            ? ` · ${data.total} en total`
            : ''}
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onExportar}
            disabled={exportar.isPending || !ubicacionActivaId}
            title="Excel con una fila por serie (y los sobrantes aparte) para informar a Plataforma"
          >
            {exportar.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="mr-1.5 h-3.5 w-3.5" />
            )}
            Exportar para Plataforma
          </Button>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition hover:border-everex-naranja hover:text-everex-azul disabled:opacity-50"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            Actualizar
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <CheckCircle2 className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="font-semibold text-everex-azul">Sin ingresos realizados</h3>
            <p className="mt-1 text-xs text-gray-600">
              {hayFiltros
                ? 'Ningún ingreso coincide con los filtros aplicados.'
                : 'Cuando confirmás o finalizás un ingreso, aparece acá.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Cierre</th>
                    <th className="px-3 py-2 text-left">N° Remito</th>
                    <th className="px-3 py-2 text-left">N° Factura</th>
                    <th className="px-3 py-2 text-left">Emisor</th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2 text-right" title="Confirmadas / declaradas">
                      Confirmadas
                    </th>
                    <th className="px-3 py-2 text-right">No recib.</th>
                    <th className="px-3 py-2 text-right">Sobrantes</th>
                    <th className="px-3 py-2 text-left">Stock</th>
                    <th className="px-3 py-2 text-left" title="anmat_task.id">
                      Acta
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  {items.map((it) => (
                    <FilaActa
                      key={it.id}
                      it={it}
                      onClick={onAbrirActa ? () => onAbrirActa(it.id) : undefined}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FilaActa({ it, onClick }: { it: RecepcionCompletada; onClick?: () => void }) {
  const clickable = Boolean(onClick);
  const estado = it.estado as EstadoActaRecepcion;
  return (
    <tr
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        'border-t border-gray-100',
        clickable && 'cursor-pointer transition hover:bg-orange-50',
      )}
    >
      <td className="whitespace-nowrap px-3 py-2">{fmtDateTime(it.cerradoAt ?? it.tracedAt)}</td>
      <td className="px-3 py-2 font-semibold text-everex-azul">{it.nRemito ?? '—'}</td>
      <td className="px-3 py-2 font-mono">{it.nFactura ?? '—'}</td>
      <td className="px-3 py-2">
        {it.razonSocialOrigen ?? '—'}
        <span className="block font-mono text-[10px] text-gray-500">{it.glnOrigen ?? ''}</span>
      </td>
      <td className="px-3 py-2">
        <span className={cn('badge', BADGE_ESTADO_ACTA[estado] ?? 'badge-gray')}>
          {ESTADO_ACTA_LABEL[estado] ?? it.estado}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        {it.totales.trazado}
        <span className="ml-1 text-gray-500">/ {it.totales.total}</span>
        {it.totales.error > 0 && (
          <span className="ml-1 text-red-700" title="Rechazadas por ANMAT">
            ({it.totales.error} rech.)
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">{it.totales.noRecibida ?? 0}</td>
      <td className="px-3 py-2 text-right">{it.sobrantes ?? 0}</td>
      <td className="px-3 py-2 text-gray-600">
        {it.stockDestino === 'na' ? 'No disponible' : it.stockDestino === 'a' ? 'Disponible' : '—'}
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-gray-500">{it.id}</td>
    </tr>
  );
}
